import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { Tooltip } from 'primeng/tooltip';
import { MouldingGraphService } from './services/moulding-graph.service';
import { MouldingItemService } from './services/moulding-item.service';
import { MouldingFlowSocketService } from './services/moulding-flow-socket.service';
import { MouldingProcessingService } from './services/moulding-processing.service';
import { MouldingNodeDialogService } from './services/moulding-node-dialog.service';
import { MouldingNodeDialogHostComponent } from './moulding-node-dialog-host/moulding-node-dialog-host.component';
import {
  MouldingFlowSummary,
  MouldingGraph,
  MouldingItem,
  MouldingStepDefinitions,
} from '../../models/moulding-item.model';
import { NodeKind } from './nodes/moulding-node-registry';

type AutosaveStatus = 'saving' | 'saved' | 'failed';

@Component({
  selector: 'app-moulding-page',
  standalone: true,
  imports: [
    ButtonModule,
    DrawerModule,
    DialogModule,
    FormsModule,
    InputTextModule,
    MessageModule,
    RadioButtonModule,
    ToggleSwitchModule,
    Tooltip,
    MouldingNodeDialogHostComponent,
  ],
  providers: [
    MouldingGraphService,
    MouldingProcessingService,
    MouldingNodeDialogService,
  ],
  templateUrl: './moulding-page.component.html',
  styleUrl: './moulding-page.component.scss',
})
export class MouldingPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('reteContainer') reteContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('flowNameInput') flowNameInput?: ElementRef<HTMLInputElement>;

  private readonly graph = inject(MouldingGraphService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly itemService = inject(MouldingItemService);
  private readonly processing = inject(MouldingProcessingService);
  private readonly nodeDialogs = inject(MouldingNodeDialogService);
  private readonly flowSocket = inject(MouldingFlowSocketService);

  private routeSub?: Subscription;
  private socketSub?: Subscription;
  private flowId: number | null = null;
  private isHydrating = false;
  private autosaveTimer: ReturnType<typeof setTimeout> | null = null;
  private autosaveStatusTimer: ReturnType<typeof setTimeout> | null = null;
  private autosaveInFlight: Promise<void> | null = null;
  private autosaveQueued = false;

  readonly flowName = signal('');
  readonly isLoading = signal(false);
  readonly error = signal<string | null>(null);
  readonly dirty = signal(false);
  readonly autosaveStatus = signal<AutosaveStatus | null>(null);
  readonly nodeCount = signal(0);
  readonly canvasZoomPercent = computed(() =>
    Math.round(this.graph.viewport().k * 100),
  );
  readonly isEditingFlowName = signal(false);
  readonly isAddNodeDrawerVisible = signal(false);
  readonly isAddFlowDialogVisible = signal(false);
  readonly runDownstreamEnabled = this.processing.runDownstreamEnabled;
  readonly downstreamExecutionActive =
    this.processing.downstreamExecutionActive;
  readonly runDownstreamHelpText =
    'When enabled, starting a processing step also runs its connected downstream steps in order.';
  readonly importableFlows = signal<MouldingFlowSummary[]>([]);
  readonly isLoadingImportableFlows = signal(false);
  readonly selectedImportFlow = signal<MouldingFlowSummary | null>(null);
  readonly nodeKinds = this.graph.getNodeKinds();

  ngOnInit(): void {
    this.graph.onChange = () => {
      this.syncNodeCount();
      if (this.isHydrating) {
        return;
      }
      this.markGraphDirty();
    };
    this.graph.onViewportChange = () => {
      if (this.isHydrating) {
        return;
      }
      this.markGraphDirty();
    };
  }

  async setRunDownstreamEnabled(enabled: boolean): Promise<void> {
    if (this.flowId === null) {
      return;
    }
    const previousValue = this.processing.runDownstreamEnabled();
    this.processing.runDownstreamEnabled.set(enabled);
    try {
      const flow = await firstValueFrom(
        this.itemService.updateItem$(this.flowId, {
          run_downstream_enabled: enabled,
        }),
      );
      this.processing.runDownstreamEnabled.set(flow.run_downstream_enabled);
    } catch {
      this.processing.runDownstreamEnabled.set(previousValue);
      this.error.set('Failed to update downstream execution setting.');
    }
  }

  async ngAfterViewInit(): Promise<void> {
    const container = this.reteContainer?.nativeElement;
    if (!container) {
      this.error.set('Unable to initialize the editor container.');
      return;
    }

    await this.graph.init(container);

    this.routeSub = this.route.paramMap.subscribe((params) => {
      const idParam = params.get('id');
      void this.loadFromRouteParam(idParam);
    });
  }

  async ngOnDestroy(): Promise<void> {
    this.nodeDialogs.close();
    this.routeSub?.unsubscribe();
    await this.flushAutosave();
    this.graph.onChange = null;
    this.graph.onViewportChange = null;
    this.clearAutosaveTimers();
    this.socketSub?.unsubscribe();
    this.processing.disconnect();
    await this.graph.destroy();
  }

  async goBack(): Promise<void> {
    await this.flushAutosave();
    await this.router.navigate(['/auto-molds']);
  }

  getNodeLabel(kind: NodeKind): string {
    return this.graph.getNodeLabel(kind);
  }

  getNodeIcon(kind: NodeKind): string {
    return this.graph.getNodeIcon(kind);
  }

  get flowIdLabel(): string {
    return this.flowId === null ? 'Auto Mold' : `AM-${this.flowId}`;
  }

  async addNode(kind: NodeKind): Promise<void> {
    await this.graph.addNode(kind);
    this.isAddNodeDrawerVisible.set(false);
  }

  get canZoomIn(): boolean {
    return this.graph.canZoomIn;
  }

  get canZoomOut(): boolean {
    return this.graph.canZoomOut;
  }

  async zoomIn(): Promise<void> {
    await this.graph.zoomIn();
  }

  async zoomOut(): Promise<void> {
    await this.graph.zoomOut();
  }

  async openAddFlowDialog(): Promise<void> {
    if (this.flowId === null) {
      return;
    }
    this.isAddFlowDialogVisible.set(true);
    this.selectedImportFlow.set(null);
    this.isLoadingImportableFlows.set(true);
    try {
      const flows = await firstValueFrom(this.itemService.getItems$());
      this.importableFlows.set(flows.filter((flow) => flow.id !== this.flowId));
    } catch {
      this.error.set('Failed to load available flows.');
      this.importableFlows.set([]);
    } finally {
      this.isLoadingImportableFlows.set(false);
    }
  }

  async importSelectedFlow(): Promise<void> {
    const source = this.selectedImportFlow();
    if (!source) {
      return;
    }
    await this.importFlow(source.id);
  }

  selectImportFlow(flow: MouldingFlowSummary): void {
    this.selectedImportFlow.set(flow);
  }

  beginFlowNameEdit(): void {
    this.isEditingFlowName.set(true);
    setTimeout(() => {
      this.flowNameInput?.nativeElement.focus();
      this.flowNameInput?.nativeElement.select();
    });
  }

  async submitFlowNameEdit(): Promise<void> {
    const input = this.flowNameInput?.nativeElement;
    if (!input) {
      return;
    }
    const name = input.value.trim();
    try {
      if (this.flowId === null || name === this.flowName().trim()) {
        return;
      }
      const flow = await firstValueFrom(
        this.itemService.updateItem$(this.flowId, { name }),
      );
      this.flowName.set(flow.name);
      this.processing.setFlowName(flow.name);
    } catch {
      input.value = this.flowName();
    } finally {
      this.isEditingFlowName.set(false);
    }
  }

  cancelFlowNameEdit(): void {
    this.isEditingFlowName.set(false);
  }

  private markGraphDirty(): void {
    this.dirty.set(true);
    if (this.autosaveInFlight) {
      this.autosaveQueued = true;
    }
    this.scheduleAutosave();
  }

  private scheduleAutosave(): void {
    if (this.autosaveTimer !== null) {
      clearTimeout(this.autosaveTimer);
    }
    this.clearAutosaveStatus();
    this.autosaveTimer = setTimeout(() => {
      this.autosaveTimer = null;
      void this.autosave();
    }, 1000);
  }

  private async autosave(): Promise<void> {
    if (this.flowId === null || !this.dirty()) {
      return;
    }
    if (this.autosaveInFlight) {
      this.autosaveQueued = true;
      return this.autosaveInFlight;
    }

    const flowId = this.flowId;
    this.autosaveStatus.set('saving');
    this.autosaveInFlight = (async () => {
      try {
        const graph = this.graph.serialize();
        await firstValueFrom(this.itemService.saveGraph$(flowId, graph));
        if (this.flowId === flowId && !this.autosaveQueued) {
          this.dirty.set(false);
        }
        this.setAutosaveStatus('saved');
      } catch {
        this.setAutosaveStatus('failed');
      }
    })();
    await this.autosaveInFlight;
    this.autosaveInFlight = null;

    if (this.autosaveQueued) {
      this.autosaveQueued = false;
      await this.autosave();
    }
  }

  private async importFlow(sourceId: number): Promise<void> {
    if (this.flowId === null) {
      return;
    }
    await this.flushAutosave();
    this.clearAutosaveTimers();
    this.isHydrating = true;
    this.error.set(null);
    try {
      const source = await firstValueFrom(this.itemService.getItem$(sourceId));
      const parsed = MouldingGraph.safeParse(source.graph ?? {});
      if (!parsed.success) {
        this.error.set('Selected flow graph is invalid.');
        return;
      }
      if (parsed.data.nodes.length === 0) {
        this.error.set('Selected flow does not contain any nodes.');
        return;
      }
      await this.graph.appendGraph(this.prepareGraphForImport(parsed.data));
      this.dirty.set(true);
      await this.autosave();
      this.isAddFlowDialogVisible.set(false);
    } catch {
      this.error.set('Failed to import the selected flow.');
    } finally {
      this.isHydrating = false;
    }
  }

  private async flushAutosave(): Promise<void> {
    if (this.autosaveTimer !== null) {
      clearTimeout(this.autosaveTimer);
      this.autosaveTimer = null;
    }
    while (this.dirty()) {
      await this.autosave();
      if (this.autosaveInFlight === null && !this.autosaveQueued) {
        break;
      }
    }
  }

  private setAutosaveStatus(status: AutosaveStatus): void {
    this.clearAutosaveStatus();
    this.autosaveStatus.set(status);
    if (status !== 'saving') {
      this.autosaveStatusTimer = setTimeout(
        () => this.autosaveStatus.set(null),
        3000,
      );
    }
  }

  private clearAutosaveStatus(): void {
    if (this.autosaveStatusTimer !== null) {
      clearTimeout(this.autosaveStatusTimer);
      this.autosaveStatusTimer = null;
    }
    this.autosaveStatus.set(null);
  }

  private clearAutosaveTimers(): void {
    if (this.autosaveTimer !== null) {
      clearTimeout(this.autosaveTimer);
      this.autosaveTimer = null;
    }
    this.autosaveQueued = false;
    this.clearAutosaveStatus();
  }

  private async loadFromRouteParam(idParam: string | null): Promise<void> {
    this.nodeDialogs.close();
    const id = Number(idParam);
    if (!idParam || !Number.isInteger(id) || id <= 0) {
      await this.flushAutosave();
      this.flowId = null;
      this.isHydrating = true;
      this.error.set('Invalid flow id in URL.');
      await this.graph.clearGraph();
      this.flowName.set('Auto Mold');
      this.dirty.set(false);
      this.clearAutosaveTimers();
      this.socketSub?.unsubscribe();
      this.processing.disconnect();
      this.isHydrating = false;
      return;
    }

    if (this.flowId !== null && this.flowId !== id) {
      await this.flushAutosave();
    }
    this.flowId = id;
    await this.loadFlow(id);
  }

  private async loadFlow(id: number): Promise<void> {
    this.isHydrating = true;
    this.isLoading.set(true);
    this.error.set(null);
    this.clearAutosaveTimers();

    try {
      const [flow, stepDefinitions] = await Promise.all([
        firstValueFrom(this.itemService.getItem$(id)),
        firstValueFrom(this.itemService.getStepDefinitions$()),
      ]);
      await this.applyFlow(flow, stepDefinitions);
      this.dirty.set(false);
      this.processing.connectToFlow(id, flow.name, flow.run_downstream_enabled);
      this.connectFlowSocket(id);
    } catch {
      this.error.set('Failed to load this flow.');
      this.flowName.set('Auto Mold');
      await this.graph.clearGraph();
    } finally {
      this.isHydrating = false;
      this.isLoading.set(false);
    }
  }

  private connectFlowSocket(flowId: number): void {
    this.socketSub?.unsubscribe();
    this.socketSub = this.flowSocket.listen(flowId).subscribe((event) => {
      if (event.type === 'node_run') {
        void this.processing.applySocketNodeRun(event.nodeRun);
      } else {
        this.processing.applyDownstreamExecutionUpdate(
          event.downstreamExecution,
        );
      }
    });
  }

  private async applyFlow(
    flow: MouldingItem,
    stepDefinitions: MouldingStepDefinitions,
  ): Promise<void> {
    this.flowName.set(flow.name || 'Untitled flow');
    this.processing.setStepParameterDefinitions(
      stepDefinitions.step_parameter_defaults,
      stepDefinitions.step_parameter_descriptions,
      stepDefinitions.step_parameter_priorities,
      stepDefinitions.step_parameter_units,
      stepDefinitions.step_descriptions,
    );

    const graph = flow.graph ?? {};
    const parsed = MouldingGraph.safeParse(graph);
    if (!parsed.success) {
      this.error.set('Flow graph data is invalid. Loaded as empty graph.');
      await this.graph.loadGraph({ nodes: [], connections: [] });
      this.syncNodeCount();
      return;
    }

    await this.graph.loadGraph(parsed.data);
    this.syncNodeCount();
    this.processing.applyNodeRuns(flow.node_runs);
  }

  private syncNodeCount(): void {
    this.nodeCount.set(this.graph.editor?.getNodes().length ?? 0);
  }

  private prepareGraphForImport(graph: MouldingGraph): MouldingGraph {
    const nodeIds = new Map(
      graph.nodes.map((node) => [node.id, crypto.randomUUID()]),
    );
    const remapNodeId = (nodeId: string): string => {
      const remappedId = nodeIds.get(nodeId);
      if (!remappedId) {
        throw new Error(
          `Graph connection references missing node "${nodeId}".`,
        );
      }
      return remappedId;
    };

    return {
      nodes: graph.nodes.map((node) => ({
        ...node,
        id: remapNodeId(node.id),
      })),
      connections: graph.connections.map((connection) => ({
        ...connection,
        source: remapNodeId(connection.source),
        target: remapNodeId(connection.target),
      })),
    };
  }
}
